import type { ApiClient } from '../client';
import { AUTH_PATHS } from '../../../config/api-paths';
import type { ApiResponse, CreateStaffLoginRequest, CreateStaffLoginResponse, CreateStaffRequest, CreateStaffResponse, ListDepartmentsResponse, ListSalaryAdjustmentsResponse, ListSalaryProfilesResponse, ListStaffDepartmentsResponse, ListStaffQuery, ListStaffTeachingAssignmentsQuery, ListStaffTeachingAssignmentsResponse, LoginRequest, LoginResponse, PaginatedListStaffResponse, ReplaceStaffTeachingAssignmentsRequest, SalarySettingsResponse, SalarySummaryResponse, StaffPaymentAccount, StaffRoleOptionsResponse, UpdateStaffRequest, Uuid } from '../types';

export function bindStaff(client: ApiClient) {
  return {
async loginBasic(body: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return client.request<LoginResponse>(AUTH_PATHS.loginBasic, {
      method: 'POST',
      body: JSON.stringify(body),
      skipAuth: true,
    });
  },

async listStaff(params?: ListStaffQuery): Promise<ApiResponse<PaginatedListStaffResponse>> {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === 'boolean') {
          qs.set(k, v ? 'true' : 'false');
          return;
        }
        const s = String(v).trim();
        if (!s) return;
        qs.set(k, s);
      });
    }
    const endpoint = qs.toString() ? `/staff?${qs.toString()}` : '/staff';
    return client.request<PaginatedListStaffResponse>(endpoint);
  },

async listStaffRoleOptions(): Promise<ApiResponse<StaffRoleOptionsResponse>> {
    return client.request<StaffRoleOptionsResponse>('/staff/role-options');
  },

async createStaff(body: CreateStaffRequest): Promise<ApiResponse<CreateStaffResponse>> {
    return client.request<CreateStaffResponse>('/staff', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async createStaffLogin(body: CreateStaffLoginRequest): Promise<ApiResponse<CreateStaffLoginResponse>> {
    return client.request<CreateStaffLoginResponse>('/staff/create-login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateStaff(body: UpdateStaffRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/staff/update', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async softDeleteStaff(staffId: Uuid): Promise<ApiResponse<void>> {
    return client.request<void>('/staff/delete', {
      method: 'POST',
      body: JSON.stringify({ staff_id: staffId }),
    });
  },

async getStaffPaymentAccount(staffId: Uuid): Promise<ApiResponse<StaffPaymentAccount>> {
    return client.request<StaffPaymentAccount>(`/staff/payment-account?staff_id=${encodeURIComponent(staffId)}`);
  },

async upsertStaffPaymentAccount(body: StaffPaymentAccount): Promise<ApiResponse<StaffPaymentAccount>> {
    return client.request<StaffPaymentAccount>('/staff/payment-account', {
      method: 'POST',
      body: JSON.stringify({
        staff_id: body.staff_id,
        method: body.method,
        account_name: body.account_name,
        account_no: body.account_no,
        bank_name: body.bank_name,
        ifsc: body.ifsc,
        upi: body.upi,
      }),
    });
  },

async listSalaryProfiles(staffId?: Uuid): Promise<ApiResponse<ListSalaryProfilesResponse>> {
    const q = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : '';
    return client.request<ListSalaryProfilesResponse>(`/staff/salary-profiles${q}`);
  },

async createSalaryProfile(body: {
    staff_id: Uuid;
    monthly_salary_cents: number;
    effective_from: string;
  }): Promise<ApiResponse<{ profile_id: Uuid }>> {
    return client.request('/staff/salary-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async recordSalaryAdvance(body: {
    staff_id: Uuid;
    amount_cents: number;
    reason?: string | null;
    period_month: string;
  }): Promise<ApiResponse<{ advance_id: Uuid }>> {
    return client.request('/staff/salary-advances', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async recordSalaryPayment(body: {
    staff_id: Uuid;
    amount_cents: number;
    method: string;
    period_month: string;
  }): Promise<ApiResponse<{ payment_id: Uuid }>> {
    return client.request('/staff/salary-payments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getSalarySummary(staffId: Uuid, period: string): Promise<ApiResponse<SalarySummaryResponse>> {
    const q = new URLSearchParams({ staff_id: staffId, period });
    return client.request<SalarySummaryResponse>(`/staff/salary-summary?${q.toString()}`);
  },

async getSalarySettings(staffId: Uuid): Promise<ApiResponse<SalarySettingsResponse>> {
    return client.request<SalarySettingsResponse>(
      `/staff/salary-settings?staff_id=${encodeURIComponent(staffId)}`
    );
  },

async upsertSalarySettings(body: { staff_id: Uuid; advance_repay_cents: number }): Promise<ApiResponse<SalarySettingsResponse>> {
    return client.request<SalarySettingsResponse>('/staff/salary-settings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listSalaryAdjustments(staffId: Uuid, period: string): Promise<ApiResponse<ListSalaryAdjustmentsResponse>> {
    const q = new URLSearchParams({ staff_id: staffId, period });
    return client.request<ListSalaryAdjustmentsResponse>(`/staff/salary-adjustments?${q.toString()}`);
  },

async recordSalaryAdjustment(body: {
    staff_id: Uuid;
    kind: string;
    amount_cents: number;
    reason?: string | null;
    period_month: string;
  }): Promise<ApiResponse<{ adjustment_id: Uuid }>> {
    return client.request('/staff/salary-adjustments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listStaffDepartments(staffId?: Uuid): Promise<ApiResponse<ListStaffDepartmentsResponse>> {
    const q = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : '';
    return client.request<ListStaffDepartmentsResponse>(`/staff/departments${q}`);
  },

async assignStaffDepartment(body: { staff_id: Uuid; department_id: Uuid }): Promise<ApiResponse<void>> {
    return client.request<void>('/staff/departments/assign', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async linkStaffUser(body: { staff_id: Uuid; user_id: Uuid }): Promise<ApiResponse<void>> {
    return client.request<void>('/staff/link-user', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async linkStaffClass(body: { staff_id: Uuid; class_id: Uuid }): Promise<ApiResponse<void>> {
    return client.request<void>('/staff/link-class', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listStaffTeachingAssignments(
    params?: ListStaffTeachingAssignmentsQuery
  ): Promise<ApiResponse<ListStaffTeachingAssignmentsResponse>> {
    const qs = new URLSearchParams();
    if (params?.staff_id) qs.set('staff_id', params.staff_id);
    if (params?.class_id) qs.set('class_id', params.class_id);
    if (params?.subject_id) qs.set('subject_id', params.subject_id);
    const endpoint = qs.toString()
      ? `/staff/teaching-assignments?${qs.toString()}`
      : '/staff/teaching-assignments';
    return client.request<ListStaffTeachingAssignmentsResponse>(endpoint);
  },

async replaceStaffTeachingAssignments(
    body: ReplaceStaffTeachingAssignmentsRequest
  ): Promise<ApiResponse<void>> {
    return client.request<void>('/staff/teaching-assignments/replace', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listDepartments(): Promise<ApiResponse<ListDepartmentsResponse>> {
    return client.request<ListDepartmentsResponse>('/departments');
  }
  };
}
